import { useState, useEffect } from 'react';
import type { LayoutBlock } from '@/mocks/editorLayout';

interface EditorRightPanelProps {
  selectedBlock: LayoutBlock | null;
  onUpdateBlock: (id: string, updates: Partial<LayoutBlock>) => void;
  onDeleteBlock: (id: string) => void;
  onDuplicateBlock: (id: string) => void;
  onLockBlock: (id: string) => void;
}

export default function EditorRightPanel({
  selectedBlock,
  onUpdateBlock,
  onDeleteBlock,
  onDuplicateBlock,
  onLockBlock,
}: EditorRightPanelProps) {
  const [localContent, setLocalContent] = useState(selectedBlock?.content || {});
  const [localSize, setLocalSize] = useState({ w: selectedBlock?.w || 4, h: selectedBlock?.h || 4 });

  useEffect(() => {
    setLocalContent(selectedBlock?.content || {});
    setLocalSize({ w: selectedBlock?.w || 4, h: selectedBlock?.h || 4 });
  }, [selectedBlock?.id]);

  if (!selectedBlock) {
    return (
      <div className="w-56 flex-shrink-0 bg-white border-l border-[#E0E0E0] flex flex-col h-full">
        <div className="px-4 py-3 border-b border-[#E0E0E0]">
          <p className="text-xs font-bold text-[#111111] font-serif uppercase tracking-wide">Propriedades</p>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
          <div className="w-12 h-12 flex items-center justify-center bg-[#F0F0F0] mb-3">
            <i className="ri-cursor-line text-[#CCCCCC] text-xl"></i>
          </div>
          <p className="text-xs text-[#999999] font-sans">Selecione um bloco no canvas para editar suas propriedades</p>
        </div>
      </div>
    );
  }

  const blockTypeLabel: Record<string, string> = {
    article: 'Artigo',
    image: 'Imagem',
    ad: 'Anúncio',
    title: 'Título',
    quote: 'Citação',
    divider: 'Divisor',
  };

  const blockTypeIcon: Record<string, string> = {
    article: 'ri-file-text-line',
    image: 'ri-image-line',
    ad: 'ri-advertisement-line',
    title: 'ri-heading',
    quote: 'ri-double-quotes-l',
    divider: 'ri-separator',
  };

  const handleContentChange = (key: string, value: string) => {
    const updated = { ...localContent, [key]: value };
    setLocalContent(updated);
    onUpdateBlock(selectedBlock.id, { content: updated });
  };

  const handleSizeChange = (key: 'w' | 'h', value: number) => {
    const updated = { ...localSize, [key]: value };
    setLocalSize(updated);
    onUpdateBlock(selectedBlock.id, { [key]: value });
  };

  return (
    <div className="w-56 flex-shrink-0 bg-white border-l border-[#E0E0E0] flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-[#E0E0E0] flex items-center gap-2">
        <div className="w-5 h-5 flex items-center justify-center">
          <i className={`${blockTypeIcon[selectedBlock.type] || 'ri-layout-line'} text-sm text-[#C41230]`}></i>
        </div>
        <p className="text-xs font-bold text-[#111111] font-serif uppercase tracking-wide flex-1">
          {blockTypeLabel[selectedBlock.type] || 'Bloco'}
        </p>
        <button
          onClick={() => onLockBlock(selectedBlock.id)}
          title={selectedBlock.locked ? 'Desbloquear' : 'Bloquear'}
          className={`w-5 h-5 flex items-center justify-center cursor-pointer transition-colors ${selectedBlock.locked ? 'text-[#C41230]' : 'text-[#CCCCCC] hover:text-[#444444]'}`}
        >
          <i className={`${selectedBlock.locked ? 'ri-lock-line' : 'ri-lock-unlock-line'} text-xs`}></i>
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">

        {/* Quick Actions */}
        <div className="flex border-b border-[#E0E0E0]">
          <button
            onClick={() => onDuplicateBlock(selectedBlock.id)}
            className="flex-1 flex items-center justify-center gap-1 py-2 text-[10px] font-sans text-[#444444] hover:bg-[#F0F0F0] cursor-pointer transition-colors"
          >
            <i className="ri-file-copy-line text-xs"></i>
            Duplicar
          </button>
          <div className="w-px bg-[#E0E0E0]"></div>
          <button
            onClick={() => onDeleteBlock(selectedBlock.id)}
            className="flex-1 flex items-center justify-center gap-1 py-2 text-[10px] font-sans text-[#C41230] hover:bg-[#FFF5F7] cursor-pointer transition-colors"
          >
            <i className="ri-delete-bin-line text-xs"></i>
            Excluir
          </button>
        </div>

        {/* Size & Position */}
        <div className="p-3 border-b border-[#E0E0E0]">
          <p className="text-[9px] font-bold text-[#999999] uppercase tracking-wide font-sans mb-2">Tamanho (colunas)</p>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[9px] text-[#999999] font-sans block mb-1">Largura</label>
              <div className="flex items-center border border-[#E0E0E0]">
                <button
                  onClick={() => handleSizeChange('w', Math.max(1, localSize.w - 1))}
                  className="w-6 h-6 flex items-center justify-center text-[#444444] hover:bg-[#F0F0F0] cursor-pointer"
                >
                  <i className="ri-subtract-line text-xs"></i>
                </button>
                <span className="flex-1 text-center text-xs font-bold text-[#111111] font-sans">{localSize.w}</span>
                <button
                  onClick={() => handleSizeChange('w', Math.min(12, localSize.w + 1))}
                  className="w-6 h-6 flex items-center justify-center text-[#444444] hover:bg-[#F0F0F0] cursor-pointer"
                >
                  <i className="ri-add-line text-xs"></i>
                </button>
              </div>
            </div>
            <div>
              <label className="text-[9px] text-[#999999] font-sans block mb-1">Altura</label>
              <div className="flex items-center border border-[#E0E0E0]">
                <button
                  onClick={() => handleSizeChange('h', Math.max(1, localSize.h - 1))}
                  className="w-6 h-6 flex items-center justify-center text-[#444444] hover:bg-[#F0F0F0] cursor-pointer"
                >
                  <i className="ri-subtract-line text-xs"></i>
                </button>
                <span className="flex-1 text-center text-xs font-bold text-[#111111] font-sans">{localSize.h}</span>
                <button
                  onClick={() => handleSizeChange('h', Math.min(20, localSize.h + 1))}
                  className="w-6 h-6 flex items-center justify-center text-[#444444] hover:bg-[#F0F0F0] cursor-pointer"
                >
                  <i className="ri-add-line text-xs"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Priority (articles) */}
        {selectedBlock.type === 'article' && (
          <div className="p-3 border-b border-[#E0E0E0]">
            <p className="text-[9px] font-bold text-[#999999] uppercase tracking-wide font-sans mb-2">Prioridade</p>
            <div className="flex gap-1">
              {(['high', 'medium', 'low'] as const).map((p) => (
                <button
                  key={p}
                  onClick={() => handleContentChange('priority', p)}
                  className={`flex-1 py-1 text-[9px] font-bold font-sans uppercase tracking-wide cursor-pointer transition-colors border ${
                    localContent.priority === p
                      ? p === 'high' ? 'bg-[#C41230] text-white border-[#C41230]'
                        : p === 'medium' ? 'bg-[#111111] text-white border-[#111111]'
                        : 'bg-[#999999] text-white border-[#999999]'
                      : 'bg-white text-[#999999] border-[#E0E0E0] hover:border-[#999999]'
                  }`}
                >
                  {p === 'high' ? 'Alta' : p === 'medium' ? 'Média' : 'Baixa'}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Style (articles/titles) */}
        {(selectedBlock.type === 'article' || selectedBlock.type === 'title') && (
          <div className="p-3 border-b border-[#E0E0E0]">
            <p className="text-[9px] font-bold text-[#999999] uppercase tracking-wide font-sans mb-2">Estilo tipográfico</p>
            <div className="space-y-1">
              {(['headline', 'subheadline', 'body', 'caption'] as const).map((s) => (
                <button
                  key={s}
                  onClick={() => handleContentChange('style', s)}
                  className={`w-full flex items-center gap-2 px-2 py-1.5 cursor-pointer transition-colors border ${
                    localContent.style === s
                      ? 'bg-[#111111] text-white border-[#111111]'
                      : 'bg-white text-[#444444] border-[#E0E0E0] hover:border-[#111111]'
                  }`}
                >
                  <span className={`font-sans text-[10px] font-bold uppercase tracking-wide flex-1 text-left`}>
                    {s === 'headline' ? 'Manchete' : s === 'subheadline' ? 'Submanchete' : s === 'body' ? 'Corpo' : 'Legenda'}
                  </span>
                  <span className={`text-[9px] font-sans opacity-60`}>
                    {s === 'headline' ? '28px' : s === 'subheadline' ? '20px' : s === 'body' ? '14px' : '11px'}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Text Content */}
        {(selectedBlock.type === 'article' || selectedBlock.type === 'title' || selectedBlock.type === 'quote') && (
          <div className="p-3 border-b border-[#E0E0E0]">
            <p className="text-[9px] font-bold text-[#999999] uppercase tracking-wide font-sans mb-2">Conteúdo</p>
            {selectedBlock.type !== 'quote' && (
              <div className="mb-2">
                <label className="text-[9px] text-[#999999] font-sans block mb-1">Título</label>
                <input
                  type="text"
                  value={localContent.title || ''}
                  onChange={(e) => handleContentChange('title', e.target.value)}
                  className="w-full border border-[#E0E0E0] px-2 py-1.5 text-xs font-sans focus:outline-none focus:border-[#111111]"
                  placeholder="Título do bloco"
                />
              </div>
            )}
            {selectedBlock.type === 'article' && (
              <>
                <div className="mb-2">
                  <label className="text-[9px] text-[#999999] font-sans block mb-1">Texto</label>
                  <textarea
                    value={localContent.body || ''}
                    onChange={(e) => handleContentChange('body', e.target.value)}
                    rows={4}
                    className="w-full border border-[#E0E0E0] px-2 py-1.5 text-xs font-sans focus:outline-none focus:border-[#111111] resize-none"
                    placeholder="Corpo do artigo..."
                  />
                </div>
                <div className="mb-2">
                  <label className="text-[9px] text-[#999999] font-sans block mb-1">Autor</label>
                  <input
                    type="text"
                    value={localContent.author || ''}
                    onChange={(e) => handleContentChange('author', e.target.value)}
                    className="w-full border border-[#E0E0E0] px-2 py-1.5 text-xs font-sans focus:outline-none focus:border-[#111111]"
                    placeholder="Nome do autor"
                  />
                </div>
                <div>
                  <label className="text-[9px] text-[#999999] font-sans block mb-1">Editoria</label>
                  <select
                    value={localContent.section || ''}
                    onChange={(e) => handleContentChange('section', e.target.value)}
                    className="w-full border border-[#E0E0E0] px-2 py-1.5 text-xs font-sans focus:outline-none focus:border-[#111111] bg-white cursor-pointer"
                  >
                    {['Política', 'Economia', 'Cultura', 'Esportes', 'Ciência', 'Saúde', 'Internacional'].map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              </>
            )}
          </div>
        )}

        {/* Image */}
        {(selectedBlock.type === 'image' || selectedBlock.type === 'article') && (
          <div className="p-3 border-b border-[#E0E0E0]">
            <p className="text-[9px] font-bold text-[#999999] uppercase tracking-wide font-sans mb-2">
              {selectedBlock.type === 'image' ? 'Imagem' : 'Foto do artigo'}
            </p>
            {localContent.imageUrl && (
              <div className="w-full h-20 overflow-hidden border border-[#E0E0E0] mb-2">
                <img src={localContent.imageUrl} alt="" className="w-full h-full object-cover object-top" />
              </div>
            )}
            <input
              type="text"
              value={localContent.imageUrl || ''}
              onChange={(e) => handleContentChange('imageUrl', e.target.value)}
              className="w-full border border-[#E0E0E0] px-2 py-1.5 text-[10px] font-sans focus:outline-none focus:border-[#111111] mb-1"
              placeholder="URL da imagem"
            />
            {selectedBlock.type === 'image' && (
              <input
                type="text"
                value={localContent.caption || ''}
                onChange={(e) => handleContentChange('caption', e.target.value)}
                className="w-full border border-[#E0E0E0] px-2 py-1.5 text-[10px] font-sans focus:outline-none focus:border-[#111111]"
                placeholder="Legenda da foto"
              />
            )}
          </div>
        )}

        {/* Block ID info */}
        <div className="p-3">
          <p className="text-[9px] text-[#CCCCCC] font-sans">ID: {selectedBlock.id}</p>
          <p className="text-[9px] text-[#CCCCCC] font-sans">Posição: col {selectedBlock.x}, linha {selectedBlock.y}</p>
        </div>
      </div>
    </div>
  );
}
